import React, { FC } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { Features } from 'generated/featureDefinitions'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
  ContractVersionsPageDocument,
  FixContractsContractVersionDocument,
  FixFlowProviderDocument,
  PayrollIntegrationsBaseA3innuvaCodesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { ValidationError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'
import type { FlashType } from 'modules/core/store/models/app'

import { manageUpperLimit } from 'modules/contracts/lib/amountStrategyUtils'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getEditedCustomFields } from 'modules/contracts/lib/customFields'

import { getFixFlowForm } from './lib/fixFlowForm'

interface FixFlowContextProps {
  form: Form
  onSubmit: (onSuccess: () => void) => void
  canEdit: boolean
  isSubmitting: boolean
  loading: boolean
  customValues: ContractFlowCustomFieldValueFragment[]
  customFields: ContractFlowCustomFieldFragment[]
}

interface FixFlowProviderProps {
  contractVersionId: number
  legalEntityId: number
  fieldIds?: number[]
  children: React.ReactNode
}

const FixFlowProviderContext = React.createContext<FixFlowContextProps | null>(null)

export const FixFlowProvider: FC<FixFlowProviderProps> = observer(
  ({ contractVersionId, legalEntityId, fieldIds, children }) => {
    const apolloClient = useApolloClient()
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const { compensations, additionalCompensations, setCustomFields, setCustomValues } =
      useContractFlow()
    const { data, loading: loadingFixFlow } = useQuery(FixFlowProviderDocument, {
      variables: {
        contractVersionId: contractVersionId,
        legalEntityId: legalEntityId,
        featureAvailabilityIds: [Features.compensations_management],
      },
    })
    const contractVersion = data?.contracts.contractVersion
    const employee = contractVersion?.employee
    const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes ?? []
    const hasCompensations =
      featureAvailabilities.find(
        (featureAvailability) => featureAvailability.id === Features.compensations_management
      )?.available ?? false
    const canEdit = contractVersion?._policies?.edit.can ?? false
    const customFields = React.useMemo(() => {
      const cf = data?.companies.legalEntitiesConnection?.nodes[0]?.customFields ?? []
      return cf.map((node) => node.field).filter(Boolean)
    }, [data])
    const customValues = React.useMemo(
      () => data?.contracts.contractVersion?.values ?? [],
      [data?.contracts.contractVersion?.values]
    )

    const [fixContractsContractVersion] = useMutation(FixContractsContractVersionDocument, {
      extractorFn: (mutationData) => mutationData.contractsMutations.fixContractsContractVersion,
    })

    // TODO: Regarding the integrations installed, we should only fetch the ones that applied to those integrations, but for now we fetch always the ones for a3innuva
    const { data: baseCodesData, loading: baseCodesLoading } = useQuery(
      PayrollIntegrationsBaseA3innuvaCodesDocument,
      {
        variables: {
          codeableId: contractVersion?.esAgreementCodeId,
          codeableType: 'Contracts::Spain::AgreementCode',
        },
        skip: !contractVersion?.esAgreementCodeId,
      }
    )
    const baseCodes = React.useMemo(
      () => baseCodesData?.payrollIntegrationsBase?.codesConnection?.nodes ?? [],
      [baseCodesData]
    )

    const form = useUnsafeMemo(() => {
      return contractVersion
        ? getFixFlowForm({
            contractVersion,
            customFields,
            customValues,
            canSeeJobTitle: contractVersion?._policies?.seeJobTitle.can ?? false,
            canSeeStartDate: contractVersion?._policies?.seeStartDate.can ?? false,
            canSeeEndDate: contractVersion?._policies?.seeEndDate.can ?? false,
            canSeeTrialPeriod: contractVersion?._policies?.seeTrialPeriod.can ?? false,
            canSeeTrialPeriodEndDate:
              contractVersion?._policies?.seeTrialPeriodEndDate.can ?? false,
            canSeeGrossSalary: contractVersion?._policies?.seeGrossSalary.can ?? false,
            canSeeWorkingHours: contractVersion?._policies?.seeWorkingHours.can ?? false,
            baseCodes,
          })
        : null
    }, [contractVersion, customFields, customValues, baseCodes])

    const customFieldsKey = React.useMemo(
      () => customFields.map((f) => f.id).join(','),
      [customFields]
    )
    const customValuesKey = React.useMemo(
      () => customValues.map((v) => v.id).join(','),
      [customValues]
    )
    const fieldIdsKey = React.useMemo(() => (fieldIds ? fieldIds.join(',') : ''), [fieldIds])

    const filteredCustomFieldsForEffect = React.useMemo(
      () => (fieldIds ? customFields.filter((field) => fieldIds.includes(field.id)) : customFields),
      [customFields, fieldIds]
    )
    const filteredCustomFieldsRef = React.useRef(filteredCustomFieldsForEffect)
    React.useEffect(() => {
      filteredCustomFieldsRef.current = filteredCustomFieldsForEffect
    }, [filteredCustomFieldsForEffect])

    const customValuesRef = React.useRef(customValues)
    React.useEffect(() => {
      customValuesRef.current = customValues
    }, [customValues])

    const setCustomFieldsRef = React.useRef(setCustomFields)
    React.useEffect(() => {
      setCustomFieldsRef.current = setCustomFields
    }, [setCustomFields])
    const setCustomValuesRef = React.useRef(setCustomValues)
    React.useEffect(() => {
      setCustomValuesRef.current = setCustomValues
    }, [setCustomValues])

    useEffect(() => {
      if (!loadingFixFlow) {
        setCustomFieldsRef.current(filteredCustomFieldsRef.current)
        setCustomValuesRef.current(customValuesRef.current)
      }
    }, [loadingFixFlow, contractVersion?.id, customFieldsKey, customValuesKey, fieldIdsKey])

    if (loadingFixFlow || baseCodesLoading) {
      return (
        <Box width="full" height="full" justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      )
    }

    if (!contractVersion || !form || !employee) return null

    const showFlashMessage = (type: FlashType, message: string): void => {
      app.setFlash({
        type,
        message,
      })
    }

    const onSubmit = async (onSuccess: () => void) => {
      if (!contractVersion.id) {
        return
      }

      setIsSubmitting(true)
      const api = ApiClient()
      form.get('payroll_policy_id').set(contractVersion.payrollPolicy?.policy?.id)
      const payload = form.serialized
      delete payload['es_tariff_group_name']

      // Filter out boolean fields with empty string values (null values from backend that weren't edited)
      // This prevents sending invalid data for fields the user doesn't have permission to see/edit
      // Boolean fields in factorial-form convert null to "" which fails backend validation
      const booleanFields = ['es_has_teleworking_contract', 'fr_forfait_jours', 'has_trial_period']
      booleanFields.forEach((field) => {
        if (payload[field] === '') {
          delete payload[field]
        }
      })

      payload.employee_id = employee.id
      payload.additional_compensations = compensations?.map(
        (compensation) => compensation.attributes
      )
      payload.custom_fields = getEditedCustomFields(form, customFields)

      if (!payload.has_trial_period) payload.trial_period_ends_on = null

      setIsSubmitting(true)
      if (hasCompensations) {
        const handled = handleResult(
          // TODO: add integration specs when setup supports it
          await fixContractsContractVersion({
            refetchQueries: [ContractVersionsPageDocument, FixFlowProviderDocument],
            awaitRefetchQueries: true,
            variables: {
              additionalCompensations: compensations?.map((compensation) => {
                const attributes = compensation.attributes
                return {
                  amount: attributes.amount,
                  calculation: attributes.calculation,
                  compensationType: attributes.compensation_type,
                  contractsTaxonomyId: attributes.contracts_taxonomy_id,
                  description: attributes.description,
                  firstPaymentOn: attributes.first_payment_on,
                  id: attributes.id,
                  minimumAmountOfHours: attributes.minimum_amount_of_hours,
                  minimumAmountOfHoursInCents: attributes.minimum_amount_of_hours_in_cents,
                  payrollPolicyId: attributes.payroll_policy_id,
                  recurrence: attributes.recurrence,
                  recurrenceCount: attributes.recurrence_count,
                  startsOn: attributes.starts_on,
                  syncWithSupplements: attributes.sync_with_supplements,
                  timeCondition: attributes.time_condition,
                  unit: attributes.unit,
                }
              }),
              newAdditionalCompensations: additionalCompensations?.map(({ tmpID: _, ...rest }) => {
                return {
                  id: rest.id,
                  amount: rest.amountStrategy?.amount,
                  amountStrategyType: rest.amountStrategyType,
                  contractVersionId: rest.contractVersionId,
                  description: rest.description,
                  firstPaymentOn: rest.firstPaymentOn,
                  payrollConceptId: rest.payrollConceptId,
                  recurrence: rest.recurrence,
                  upperLimit: manageUpperLimit(rest.amountStrategy),
                }
              }),
              customFields: payload.custom_fields,
              employeeId: employee.id,
              id: contractVersion.id,
              contractsEsTariffGroupId: payload.contracts_es_tariff_group_id,
              deActivityType: payload.de_activity_type,
              deBaseSalaryTypeId: payload.de_base_salary_type_id,
              deContractTypeId: payload.de_contract_type_id,
              deEmploymentType: payload.de_employment_type,
              deFlatRateTax: payload.de_flat_rate_tax,
              dePersonalKeyGroupId: payload.de_personal_key_group_id,
              effectiveOn: payload.effective_on,
              endsOn: payload.ends_on,
              esA3innuvaJobPositionId: payload.es_a3innuva_job_position_id,
              esA3innuvaJobPositionPayrollIntegrationCode:
                payload.es_a3innuva_job_position_payroll_integration_code,
              esA3innuvaJobPositionPayrollIntegrationName:
                payload.es_a3innuva_job_position_payroll_integration_name,
              esAgreementCodePayrollIntegrationCode:
                payload.es_agreement_code_payroll_integration_code,
              esAgreementCodePayrollIntegrationName:
                payload.es_agreement_code_payroll_integration_name,
              esClassificationId: payload.es_classification_id,
              esClassificationPayrollIntegrationCode:
                payload.es_classification_payroll_integration_code,
              esClassificationPayrollIntegrationName:
                payload.es_classification_payroll_integration_name,
              esCnoOccupationId: payload.es_cno_occupation_id,
              esCnoOccupationPayrollIntegrationCode:
                payload.es_cno_occupation_payroll_integration_code,
              esCnoOccupationPayrollIntegrationName:
                payload.es_cno_occupation_payroll_integration_name,
              esContractObservations: payload.es_contract_observations,
              esContractTypeId: payload.es_contract_type_id,
              esContractTypePayrollIntegrationCode:
                payload.es_contract_type_payroll_integration_code,
              esContractTypePayrollIntegrationName:
                payload.es_contract_type_payroll_integration_name,
              esContributionTypeId: payload.es_contribution_type_id,
              esContributionTypePayrollIntegrationCode:
                payload.es_contribution_type_payroll_integration_code,
              esContributionTypePayrollIntegrationName:
                payload.es_contribution_type_payroll_integration_name,
              esCotizationGroup: payload.es_cotization_group,
              esEducationLevelId: payload.es_education_level_id,
              esEducationLevelPayrollIntegrationCode:
                payload.es_education_level_payroll_integration_code,
              esEducationLevelPayrollIntegrationName:
                payload.es_education_level_payroll_integration_name,
              esHasTeleworkingContract: payload.es_has_teleworking_contract,
              esJobDescription: payload.es_job_description,
              esOccupationCodeId: payload.es_occupation_code_id,
              esOccupationCodePayrollIntegrationCode:
                payload.es_occupation_code_payroll_integration_code,
              esOccupationCodePayrollIntegrationName:
                payload.es_occupation_code_payroll_integration_name,
              esProfessionalCategoryId: payload.es_professional_category_id,
              esRegimeId: payload.es_regime_id,
              esRegimePayrollIntegrationCode: payload.es_regime_payroll_integration_code,
              esRegimePayrollIntegrationName: payload.es_regime_payroll_integration_name,
              esTariffGroupId: payload.es_tariff_group_id
                ? String(payload.es_tariff_group_id)
                : null,
              esWorkingDayTypeId: payload.es_working_day_type_id,
              frCoefficient: payload.fr_coefficient,
              frContractTypeId: payload.fr_contract_type_id,
              frEmployeeType: payload.fr_employee_type,
              frForfaitJours: payload.fr_forfait_jours,
              frJoursParAn: payload.fr_jours_par_an,
              frJoursParAnCents: payload.fr_jours_par_an_cents,
              frLevelId: payload.fr_level_id,
              frMutualId: payload.fr_mutual_id,
              frProfessionalCategoryId: payload.fr_professional_category_id,
              frStepId: payload.fr_step_id,
              frWorkTypeId: payload.fr_work_type_id,
              hasTrialPeriod: payload.has_trial_period,
              jobCatalogLevelId: payload.job_catalog_level_id,
              jobCatalogTreeNodeUuid: payload.job_catalog_tree_node_uuid,
              jobTitle: payload.job_title,
              maxLegalYearlyHours: payload.max_legal_yearly_hours,
              maximumWeeklyHours: payload.maximum_weekly_hours,
              minRestMinutesBetweenDays: payload.min_rest_minutes_between_days,
              maxWorkMinutesPerDay: payload.max_work_minutes_per_day,
              ptContractTypeId: payload.pt_contract_type_id,
              salaryAmount: payload.salary_amount,
              salaryFrequency: payload.salary_frequency,
              startsOn: payload.starts_on,
              trialPeriodEndsOn: payload.trial_period_ends_on,
              workingHours: payload.working_hours,
              workingHoursFrequency: payload.working_hours_frequency,
              workingWeekDays: payload.working_week_days,
              workingTimePercentageInCents: payload.working_time_percentage_in_cents,
              bankHolidayTreatment: payload.bank_holiday_treatment,
              annualWorkingTimeDistribution: payload.annual_working_time_distribution,
            },
          }),
          (errors) => {
            form.setErrors(errors)
            showFlashMessage(
              'wrong',
              Object.values(errors).join(', ') ?? i18n.t('contract_versions.flows.errors.unknown')
            )
            setIsSubmitting(false)
          }
        )

        if (handled.success) {
          showFlashMessage(
            'correct',
            i18n.t('contract_versions.flows.fix.confirmation', {
              employeeFullName: employee.fullName,
            })
          )
          form.cleanAll()
          setIsSubmitting(false)
          onSuccess()
        }

        return handled
      } else {
        api
          .post(`/api/rest/contracts/contract_versions/fix`, payload)
          .then(() => {
            apolloClient.refetchQueries({
              include: [ContractVersionsPageDocument, FixFlowProviderDocument],
            })
            showFlashMessage(
              'correct',
              i18n.t('contract_versions.flows.fix.confirmation', {
                employeeFullName: employee.fullName,
              })
            )
            onSuccess()
          })
          .catch((e) => {
            if (e instanceof ValidationError) showFlashMessage('wrong', e.message)
            else showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
          })
          .finally(() => {
            form.cleanAll()
            setIsSubmitting(false)
          })
      }
    }

    return (
      <FixFlowProviderContext.Provider
        value={{
          form,
          canEdit,
          onSubmit,
          isSubmitting,
          loading: loadingFixFlow,
          customFields: customFields,
          customValues: customValues,
        }}
      >
        {children}
      </FixFlowProviderContext.Provider>
    )
  }
)

export const useFixFlowProvider = (): FixFlowContextProps => {
  const fixFlow = React.useContext(FixFlowProviderContext)

  if (!fixFlow) {
    throw new Error('useFixFlowProvider must be used within a FixFlowProvider')
  }

  return fixFlow
}
